"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Sparkles, Flame, Crown, Cat } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function QueenOfWandsPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Değnekler</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Değnekler Kraliçesi</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Değnekler Kraliçesi</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/queen-of-wands.svg"
                  alt="Queen of Wands Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-700 rounded-full flex items-center justify-center">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Queen of Wands</h1>
                  <p className="text-xl text-red-400">Değnekler Kraliçesi / Güçlü Lider</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Güçlü liderlik, özgüven ve yaratıcı enerji kartı. Charismatik,
                bağımsız ve başkalarına ilham veren güçlü bir kadın enerjisini temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Liderlik</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Özgüven</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Charisma</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Bağımsızlık</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güçlü Liderlik</h3>
                  <p className="text-slate-400 text-sm">Doğal otorite, ilham verme ve yönetim becerisi.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Özgüven ve Karizma</h3>
                  <p className="text-slate-400 text-sm">Kendinden emin duruş, çekicilik ve etkileyici kişilik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yaratıcı Enerji</h3>
                  <p className="text-slate-400 text-sm">Sanatsal yetenek, yenilikçi düşünce ve ifade gücü.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Bağımsızlık</h3>
                  <p className="text-slate-400 text-sm">Özgürlük, kendi ayakları üzerinde durma.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Baskıcı Liderlik</h3>
                  <p className="text-slate-400 text-sm">Otokratik yaklaşım, başkalarını ezme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı Ego</h3>
                  <p className="text-slate-400 text-sm">Kibir, kendini beğenmişlik ve manipülasyon.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güvensizlik</h3>
                  <p className="text-slate-400 text-sim">Özgüven eksikliği, içsel şüpheler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kıskançlık</h3>
                  <p className="text-slate-400 text-sm">Rekabetçi tavır, başkalarını kıskanma.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Güçlü ve bağımsız partner</li>
                <li>• İlişkide liderlik alma</li>
                <li>• Tutkulu ve sadık aşk</li>
                <li>• Özgüven saçan enerji</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Liderlik pozisyonu</li>
                <li>• Girişimcilik başarısı</li>
                <li>• Yaratıcı endüstriler</li>
                <li>• Mentor ve rehber</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Card Advice */}
        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-6 mb-8 border border-red-500/20">
          <h3 className="text-lg font-semibold text-red-200 mb-3">💡 Kart Tavsiyesi</h3>
          <p className="text-slate-200 leading-relaxed">
            İç gücünüzü keşfedin ve liderlik becerilerinizi geliştirin. Özgüven sahibi
            olun ama kibirle karıştırmayın. Başkalarına ilham verin ve onları
            destekleyin. Yaratıcı enerjinizi pozitif yönde kullanın.
          </p>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/wands/knight-of-wands"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Değnekler Şövalyesi
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/wands/king-of-wands"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Değnekler Kralı
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}